import { apiService } from './api'
import { Reminder, ReminderFormData, ReminderDashboard, PaginatedResponse } from '../types'

export class ReminderService {
  async getReminders(
    page = 1,
    limit = 50,
    filters?: {
      is_completed?: boolean
      recurrence?: string
      date_from?: string
      date_to?: string
      overdue?: boolean
      upcoming?: boolean
    }
  ): Promise<PaginatedResponse<Reminder>> {
    const params = { page, limit, ...filters }
    const response = await apiService.get<PaginatedResponse<Reminder>>('/reminders', params)
    return response
  }

  async getReminder(id: number): Promise<Reminder> {
    const response = await apiService.get<{ reminder: Reminder }>(`/reminders/${id}`)
    return response.reminder
  }

  async createReminder(data: ReminderFormData): Promise<Reminder> {
    // Convert camelCase to snake_case for backend API
    const backendData = {
      title: data.title,
      description: data.description,
      amount: data.amount,
      reminder_date: data.reminderDate,
      is_recurring: data.isRecurring,
      recurrence_type: data.recurrenceType,
      recurrence_interval: data.recurrenceInterval
    }
    
    const response = await apiService.post<{ reminder: Reminder }>('/reminders', backendData)
    return response.reminder
  }

  async updateReminder(id: number, data: Partial<ReminderFormData>): Promise<Reminder> {
    // Convert camelCase to snake_case for backend API
    const backendData: any = {}
    
    if (data.title !== undefined) backendData.title = data.title
    if (data.description !== undefined) backendData.description = data.description
    if (data.amount !== undefined) backendData.amount = data.amount
    if (data.reminderDate !== undefined) backendData.reminder_date = data.reminderDate
    if (data.isRecurring !== undefined) backendData.is_recurring = data.isRecurring
    if (data.recurrenceType !== undefined) backendData.recurrence_type = data.recurrenceType
    if (data.recurrenceInterval !== undefined) backendData.recurrence_interval = data.recurrenceInterval
    
    const response = await apiService.put<{ reminder: Reminder }>(`/reminders/${id}`, backendData)
    return response.reminder
  }

  async deleteReminder(id: number): Promise<void> {
    return apiService.delete<void>(`/reminders/${id}`)
  }

  async completeReminder(id: number): Promise<Reminder> {
    const response = await apiService.put<{ reminder: Reminder }>(`/reminders/${id}/complete`, {})
    return response.reminder
  }

  async snoozeReminder(id: number, snoozeUntil: string): Promise<Reminder> {
    const response = await apiService.put<{ reminder: Reminder }>(`/reminders/${id}/snooze`, { snooze_until: snoozeUntil })
    return response.reminder
  }

  async getOverdueReminders(): Promise<Reminder[]> {
    const response = await apiService.get<{ reminders: Reminder[] }>('/reminders/overdue')
    return response.reminders
  }

  async getUpcomingReminders(days = 7): Promise<Reminder[]> {
    const response = await apiService.get<{ reminders: Reminder[] }>('/reminders/upcoming', { days })
    return response.reminders
  }

  async getTodayReminders(): Promise<Reminder[]> {
    const response = await apiService.get<{ reminders: Reminder[] }>('/reminders/today')
    return response.reminders
  }

  async getDashboard(): Promise<ReminderDashboard> {
    const response = await apiService.get<{ summary: ReminderDashboard }>('/reminders/dashboard')
    return response.summary
  }

  async bulkCreateReminders(reminders: ReminderFormData[]): Promise<{
    created: Reminder[]
    errors: Array<{ index: number; error: string; data: ReminderFormData }>
    successCount: number
    errorCount: number
  }> {
    // Convert all reminders to backend format
    const backendReminders = reminders.map(data => ({
      title: data.title,
      description: data.description,
      amount: data.amount,
      reminder_date: data.reminderDate,
      is_recurring: data.isRecurring,
      recurrence_type: data.recurrenceType,
      recurrence_interval: data.recurrenceInterval
    }))
    
    const response = await apiService.post<any>('/reminders/bulk', { reminders: backendReminders })
    return response
  }
}

export const reminderService = new ReminderService()